import {
  BOARD_SIZE,
  idx,
  type Layout,
  type Tile,
  type TileKind,
  type TowerKind,
} from "@empire/core-sim";

/**
 * A maze drawn as text, one string per board row.
 *
 *   .  empty   #  road   ~  mud   B  barricade   C  core   G  gate (road)
 *   a  Arrow   l  Ballista   b  Brazier   f  Frost   c  Cannon  (each on a slot)
 *   s  an empty tower slot
 *
 * Used by the barbarian villages (D-113) and the Trials (29 Sep 2026), so a
 * hand-made maze reads like a picture of itself.
 */
const TOWER: Record<string, TowerKind> = {
  a: "arrow",
  l: "ballista",
  b: "brazier",
  f: "frost",
  c: "cannon",
};

const TILE: Record<string, TileKind> = {
  ".": "empty",
  "#": "road",
  "~": "mud",
  B: "barricade",
  C: "core",
  G: "road",
  s: "tower_slot",
};

export function layoutFromPicture(
  rows: readonly string[],
  levels: { wallLevel: number; engineersGuildLevel: number },
  name = "picture"
): Layout {
  if (rows.length !== BOARD_SIZE) throw new Error(`${name}: ${rows.length} rows, expected ${BOARD_SIZE}`);
  const tiles: Tile[] = new Array(BOARD_SIZE * BOARD_SIZE);
  let gate: { x: number; y: number } | null = null;
  rows.forEach((row, y) => {
    if (row.length !== BOARD_SIZE) throw new Error(`${name}: row ${y} is ${row.length} wide`);
    [...row].forEach((char, x) => {
      const tower = TOWER[char];
      if (tower) {
        tiles[idx(x, y, BOARD_SIZE)] = { kind: "tower_slot", tower: { kind: tower, level: 1 } };
        return;
      }
      const kind = TILE[char];
      if (!kind) throw new Error(`${name}: unknown "${char}" at ${x},${y}`);
      tiles[idx(x, y, BOARD_SIZE)] = { kind };
      if (char === "G") gate = { x, y };
    });
  });
  if (!gate) throw new Error(`${name}: no gate`);
  return { size: BOARD_SIZE, tiles, gate, ...levels };
}
